import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import { ClipLoader } from "react-spinners";
import { FaFileUpload } from "react-icons/fa";

import api from "../../../services/api";
import { useToast } from "../../../hooks/toast";
import { useCompanies } from "../../../hooks/companies";
import { enviarAnexos, mensagemErroAnexo } from "../../../services/freteTabelaAnexosApi";
import AnexosTabela from "../TabelasFrete/AnexosTabela";
import EscolherArquivos from "components/EscolherArquivos";

const PREVIEW_URL = "/embarcadores/fretetabela/preview-xlsm";
const IMPORT_URL = "/embarcadores/fretetabela/import-xlsm";
const TABELAS_LIST_URL = "/frete/tabelas";

const formatDate = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("pt-BR");
};

const formatTabelaLabel = (tabela) => {
  if (!tabela || typeof tabela !== "object") return "";
  const partes = [tabela.nome];
  const vigencia = [formatDate(tabela.dataValIni), formatDate(tabela.dataValFim)]
    .filter(Boolean)
    .join(" → ");
  if (vigencia) partes.push(`(${vigencia})`);
  if (tabela.id) partes.push(`#${tabela.id}`);
  return partes.filter(Boolean).join(" ");
};

const getBackendMessage = (error) => {
  const payload = error?.response?.data;
  if (typeof payload === "string" && payload.trim()) {
    return payload;
  }

  if (payload && typeof payload === "object") {
    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message;
    }
    if (typeof payload.error === "string" && payload.error.trim()) {
      return payload.error;
    }
  }

  return "";
};

const buildErrorMessage = (error) => {
  const status = error?.response?.status;
  const backendMessage = getBackendMessage(error);

  if (status === 400) {
    return backendMessage || "Arquivo inválido ou campos obrigatórios ausentes";
  }

  if (status === 404) {
    return "Tabela de preço não encontrada. Verifique o nome informado.";
  }

  if (status === 409) {
    return "O nome informado corresponde a mais de uma tabela. Refine o nome para ficar único.";
  }

  if (status === 500) {
    return "Erro interno ao processar a planilha. Tente novamente.";
  }

  return backendMessage || "Não foi possível processar a importação. Tente novamente.";
};

const normalizeWarnings = (warnings) => {
  if (!Array.isArray(warnings)) return [];

  return warnings
    .map((warning) => {
      if (typeof warning === "string") return warning;
      if (warning && typeof warning === "object") {
        return JSON.stringify(warning);
      }
      return String(warning);
    })
    .filter(Boolean);
};

const ImportFreteTabelaXlsm = () => {
  const [nomeTabela, setNomeTabela] = useState("");
  // PO1-2312: "Tabela de destino" opcional, por id, aceita tabela inativa. É como a
  // Everlog preenche a tabela que nasce inativa a partir do envio da transportadora.
  const [idFreteTabela, setIdFreteTabela] = useState("");
  const [tabelas, setTabelas] = useState([]);
  const [loadingTabelas, setLoadingTabelas] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  // PO1-2859: id da tabela cujo XLSM foi anexado (remonta o bloco de anexos após o envio).
  const [anexoImportadoId, setAnexoImportadoId] = useState(null);

  const { addToast } = useToast();
  const { empresa, transportadora } = useCompanies();
  const empresaId =
    empresa && empresa.value !== undefined ? empresa.value : null;
  const transportadoraId =
    transportadora && transportadora.value !== undefined
      ? transportadora.value
      : null;
  const alertRef = useRef(null);

  const isBusy = loadingPreview || loadingImport;
  const hasRequiredData = Boolean(selectedFile) && (Boolean(idFreteTabela) || Boolean(nomeTabela.trim()));
  const tabelasAtivas = useMemo(() => tabelas.filter((t) => t && t.status), [tabelas]);

  // Carrega as tabelas do embarcador + transportadora selecionados no header.
  // Sem embarcador, não busca nada (endpoint exige idEmbarcador).
  useEffect(() => {
    let cancelled = false;
    const fetchTabelas = async () => {
      if (!empresaId) {
        setTabelas([]);
        return;
      }
      try {
        setLoadingTabelas(true);
        // Sem filtro de status: a lista de destino inclui as inativas; a lista por nome usa só as ativas.
        const params = { idEmbarcador: empresaId, limit: 200 };
        if (transportadoraId) params.idTransportadora = transportadoraId;
        const response = await api.get(TABELAS_LIST_URL, { params });
        if (cancelled) return;
        const rows = Array.isArray(response.data && response.data.rows)
          ? response.data.rows
          : [];
        setTabelas(rows);
      } catch (error) {
        if (cancelled) return;
        console.error("[ImportFreteTabelaXlsm] Erro ao listar tabelas", error);
        setTabelas([]);
      } finally {
        if (!cancelled) setLoadingTabelas(false);
      }
    };
    fetchTabelas();
    return () => {
      cancelled = true;
    };
  }, [empresaId, transportadoraId]);

  // Se a tabela atualmente selecionada deixar de existir na lista (mudou
  // empresa/transportadora), limpa para evitar submeter um nome órfão.
  useEffect(() => {
    if (!nomeTabela) return;
    const existe = tabelas.some((t) => t && t.nome === nomeTabela);
    if (!existe) setNomeTabela("");
  }, [tabelas, nomeTabela]);

  useEffect(() => {
    if (!idFreteTabela) return;
    const existe = tabelas.some((t) => t && String(t.id) === String(idFreteTabela));
    if (!existe) setIdFreteTabela("");
  }, [tabelas, idFreteTabela]);

  const validRows = previewResult?.validRows || {};
  const validCustos = Number(validRows?.custos || 0);
  const validTaxas = Number(validRows?.taxas || 0);
  const hasValidRows = validCustos > 0 || validTaxas > 0;
  const canImport = Boolean(previewResult) && hasValidRows;

  const previewWarnings = useMemo(
    () => normalizeWarnings(previewResult?.warnings),
    [previewResult]
  );
  const importWarnings = useMemo(
    () => normalizeWarnings(importResult?.warnings),
    [importResult]
  );

  useEffect(() => {
    setPreviewResult(null);
    setImportResult(null);
    setAnexoImportadoId(null);
  }, [selectedFile, nomeTabela, idFreteTabela]);

  const buildFormData = useCallback(
    ({ includeDryRun = false } = {}) => {
      const formData = new FormData();
      formData.append("file", selectedFile);
      if (idFreteTabela) formData.append("idFreteTabela", String(idFreteTabela));
      if (nomeTabela.trim()) formData.append("nomeTabela", nomeTabela.trim());
      // PO1-2859: o backend resolve a tabela pelo nome SÓ dentro do embarcador ativo e usa o
      // usuário logado como creatorUserId (o campo do cliente é ignorado desde a H1).
      if (empresaId !== null && empresaId !== undefined) {
        formData.append("idEmbarcador", String(empresaId));
      }

      if (includeDryRun) {
        formData.append("dryRun", "false");
      }

      return formData;
    },
    [selectedFile, nomeTabela, idFreteTabela, empresaId]
  );

  const notify = useCallback(
    (type, title, description) => {
      addToast({
        place: "tr",
        type,
        title,
        description,
        icon:
          type === "success"
            ? "tim-icons icon-check-2"
            : "tim-icons icon-alert-circle-exc",
      });
    },
    [addToast]
  );

  const handlePreview = async () => {
    if (!hasRequiredData || isBusy) return;

    try {
      setLoadingPreview(true);
      const formData = buildFormData();
      console.log("[ImportFreteTabelaXlsm] Preview start", {
        url: PREVIEW_URL,
        baseURL: api?.defaults?.baseURL,
        nomeTabela: nomeTabela.trim(),
        fileName: selectedFile?.name,
        fileSize: selectedFile?.size,
        idEmbarcador: empresaId,
      });
      const response = await api.post(PREVIEW_URL, formData);
      console.log("[ImportFreteTabelaXlsm] Preview success", {
        status: response?.status,
        data: response?.data,
      });
      setPreviewResult(response.data || null);
      setImportResult(null);

      notify("success", "Pré-visualização concluída", "Arquivo analisado com sucesso.");
    } catch (error) {
      console.error("[ImportFreteTabelaXlsm] Preview error", {
        message: error?.message,
        code: error?.code,
        status: error?.response?.status,
        responseData: error?.response?.data,
        requestUrl: error?.config?.url,
        requestBaseURL: error?.config?.baseURL,
      });
      const description = buildErrorMessage(error);
      notify("danger", "Erro ao pré-visualizar", description);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleImport = async () => {
    if (!canImport || isBusy) return;

    try {
      setLoadingImport(true);
      const response = await api.post(IMPORT_URL, buildFormData({ includeDryRun: true }));
      setImportResult(response.data || null);
      notify("success", "Importação concluída", "Planilha importada com sucesso.");

      // PO1-2859: a planilha importada vira o anexo original da tabela (best-effort).
      const idTabelaImportada = response.data && response.data.idFreteTabela;
      if (idTabelaImportada && selectedFile) {
        try {
          await enviarAnexos(idTabelaImportada, [selectedFile]);
          setAnexoImportadoId(idTabelaImportada);
          notify("success", "Planilha anexada", "O XLSM foi guardado como anexo original da tabela.");
        } catch (anexoError) {
          notify(
            "warning",
            "Planilha importada, anexo falhou",
            mensagemErroAnexo(anexoError, "Não foi possível anexar a planilha à tabela.")
          );
        }
      }
    } catch (error) {
      const description = buildErrorMessage(error);
      notify("danger", "Erro ao importar", description);
    } finally {
      setLoadingImport(false);
    }
  };

  const handleReset = () => {
    setNomeTabela("");
    setSelectedFile(null);
    setPreviewResult(null);
    setImportResult(null);
    setFileInputKey((current) => current + 1);
  };

  return (
    <div className="content">
      <div className="rna-container">
        <NotificationAlert ref={alertRef} />
      </div>

      <Row>
        <Col className="ml-auto mr-auto">
          <h2 className="text-center">Importar Tabela de Preço (XLSM)</h2>
        </Col>
      </Row>

      <Row>
        <Col lg="8" className="ml-auto mr-auto">
          <Card>
            <CardBody>
              <FormGroup>
                <Label>Tabela de destino (opcional, aceita inativa)</Label>
                <Input
                  type="select"
                  value={idFreteTabela}
                  disabled={isBusy || loadingTabelas || !empresaId || tabelas.length === 0}
                  onChange={(event) => {
                    setIdFreteTabela(event.target.value);
                    if (event.target.value) setNomeTabela("");
                  }}
                >
                  <option value="">— Sem tabela de destino (usar a tabela ativa pelo nome) —</option>
                  {tabelas.map((tabela) => (
                    <option key={`dest-${tabela.id}`} value={tabela.id}>
                      {formatTabelaLabel(tabela)}{tabela.status ? "" : " (inativa)"}
                    </option>
                  ))}
                </Input>
                <small className="text-muted d-block mt-1">
                  Use para preencher uma tabela específica, inclusive a que nasceu inativa a partir do envio da transportadora pelo MeuFrete.
                </small>
              </FormGroup>

              <FormGroup>
                <Label>Tabela</Label>
                <Input
                  type="select"
                  value={nomeTabela}
                  disabled={isBusy || loadingTabelas || !empresaId || tabelasAtivas.length === 0 || Boolean(idFreteTabela)}
                  onChange={(event) => setNomeTabela(event.target.value)}
                >
                  <option value="">
                    {!empresaId
                      ? "Selecione um embarcador no topo da página"
                      : loadingTabelas
                        ? "Carregando tabelas..."
                        : tabelasAtivas.length === 0
                          ? transportadoraId
                            ? "Nenhuma tabela ativa para esta transportadora"
                            : "Nenhuma tabela ativa para este embarcador"
                          : idFreteTabela
                            ? "Definida pela tabela de destino"
                            : "— Selecione a tabela —"}
                  </option>
                  {tabelasAtivas.map((tabela) => (
                    <option key={tabela.id} value={tabela.nome}>
                      {formatTabelaLabel(tabela)}
                    </option>
                  ))}
                </Input>
                <small className="text-muted d-block mt-1">
                  {transportadoraId
                    ? `Filtrado pela transportadora selecionada no topo (${tabelasAtivas.length} tabela${tabelasAtivas.length === 1 ? "" : "s"})`
                    : "Mostrando todas as transportadoras do embarcador. Selecione uma no topo para filtrar."}
                </small>
              </FormGroup>

              <FormGroup>
                <Label>Arquivo XLSM</Label>
                {/* PO1-3887: o tema esconde o input file dentro de .form-group. */}
                <EscolherArquivos
                  key={fileInputKey}
                  accept=".xlsm"
                  disabled={isBusy}
                  ariaLabel="Arquivo XLSM"
                  rotulo="Escolher arquivo"
                  onSelecionar={(files) => setSelectedFile(files && files.length ? files[0] : null)}
                />
                {selectedFile ? (
                  <small className="text-muted d-block mt-2">
                    Arquivo selecionado: {selectedFile.name}
                  </small>
                ) : null}
              </FormGroup>

              <div className="text-center mt-4">
                <Button
                  className="btn btn-round btn-info"
                  onClick={handlePreview}
                  disabled={!hasRequiredData || isBusy}
                >
                  Pré-visualizar
                </Button>

                <Button
                  className="btn btn-round btn-success"
                  onClick={handleImport}
                  disabled={!canImport || isBusy}
                >
                  <FaFileUpload size={18} />
                  {"  "} Importar
                </Button>

                <Button
                  className="btn btn-round btn-primary"
                  onClick={handleReset}
                  disabled={isBusy}
                >
                  Nova importação
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {isBusy ? (
        <Row>
          <Col className="ml-auto mr-auto text-center">
            <ClipLoader sizeUnit="px" size={80} color="#1d8cf8" loading={isBusy} />
            <br />
            <br />
            <h4 className="text-info">
              {loadingPreview ? "Processando pré-visualização..." : "Importando planilha..."}
            </h4>
          </Col>
        </Row>
      ) : null}

      {previewResult ? (
        <Row>
          <Col lg="8" className="ml-auto mr-auto">
            <Card>
              <CardBody>
                <h4>Resultado da Pré-visualização</h4>
                <p>
                  <strong>idFreteTabela:</strong> {String(previewResult?.idFreteTabela || "-")}
                </p>
                <p>
                  <strong>Linhas válidas (custos):</strong> {validCustos}
                </p>
                <p>
                  <strong>Linhas válidas (taxas):</strong> {validTaxas}
                </p>

                {!hasValidRows ? (
                  <div className="alert alert-warning">
                    Nenhuma linha válida para importação
                  </div>
                ) : null}

                <h5>Warnings</h5>
                {previewWarnings.length > 0 ? (
                  <ul>
                    {previewWarnings.map((warning, index) => (
                      <li key={`preview-warning-${index}`}>{warning}</li>
                    ))}
                  </ul>
                ) : (
                  <p>Nenhum warning encontrado.</p>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      ) : null}

      {importResult ? (
        <Row>
          <Col lg="8" className="ml-auto mr-auto">
            <Card>
              <CardBody>
                <h4>Resultado da Importação</h4>
                <p>
                  <strong>idFreteTabela:</strong> {String(importResult?.idFreteTabela || "-")}
                </p>
                <p>
                  <strong>Inseridos (custos):</strong>{" "}
                  {Number(importResult?.inserted?.custos || 0)}
                </p>
                <p>
                  <strong>Inseridos (taxas):</strong>{" "}
                  {Number(importResult?.inserted?.taxas || 0)}
                </p>

                {importResult?.idFreteTabela ? (
                  <AnexosTabela
                    key={anexoImportadoId || "sem-anexo"}
                    tabelaId={importResult.idFreteTabela}
                    descricao="A planilha importada foi anexada como arquivo original; adicione aqui o e-mail do carrier, se houver."
                  />
                ) : null}

                <h5>Warnings</h5>
                {importWarnings.length > 0 ? (
                  <ul>
                    {importWarnings.map((warning, index) => (
                      <li key={`import-warning-${index}`}>{warning}</li>
                    ))}
                  </ul>
                ) : (
                  <p>Nenhum warning encontrado.</p>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      ) : null}
    </div>
  );
};

export default ImportFreteTabelaXlsm;
